import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/6TdzGPbST4y3Gk6CzyrcQT/IMG_6635.jpg';
type Phase = 'ecg' | 'logo' | 'language';
type Language = 'es' | 'en' | null;
interface SplashScreenProps {
  onComplete: (language: 'es' | 'en') => void;
}
export function SplashScreen({
  onComplete
}: SplashScreenProps) {
  const [phase, setPhase] = useState<Phase>('ecg');
  useEffect(() => {
    // Phase 1: ECG animation (3.5 seconds — give it time to breathe)
    const ecgTimer = setTimeout(() => {
      setPhase('logo');
    }, 3500);
    return () => clearTimeout(ecgTimer);
  }, []);
  useEffect(() => {
    if (phase === 'logo') {
      // Phase 2: Logo reveal (2.8 seconds — smooth, deliberate reveal)
      const logoTimer = setTimeout(() => {
        setPhase('language');
      }, 2800);
      return () => clearTimeout(logoTimer);
    }
  }, [phase]);
  const handleLanguageSelect = (lang: 'es' | 'en') => {
    onComplete(lang);
  };
  return <motion.div className="fixed inset-0 z-[100] bg-[#070707] flex items-center justify-center" exit={{
    opacity: 0
  }} transition={{
    duration: 0.6
  }} data-id="element-273">
      <AnimatePresence mode="wait" data-id="element-274">
        {/* Phase 1: ECG Animation */}
        {phase === 'ecg' && <motion.div key="ecg" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.5
      }} className="relative w-full max-w-2xl px-8" data-id="element-275">
            <ECGAnimation data-id="element-276" />
          </motion.div>}

        {/* Phase 2: Logo Reveal */}
        {phase === 'logo' && <motion.div key="logo" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.5
      }} className="flex flex-col items-center" data-id="element-277">
            <LogoReveal data-id="element-278" />
          </motion.div>}

        {/* Phase 3: Language Selector */}
        {phase === 'language' && <motion.div key="language" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.6
      }} className="flex flex-col items-center px-6" data-id="element-279">
            <motion.img src={LOGO_URL} alt="Doctor Ecommerce" className="h-24 md:h-32 mb-12 rounded-xl object-contain" style={{
          imageRendering: 'auto'
        }} initial={{
          opacity: 0,
          y: -20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-280" />

            <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.2,
          duration: 0.5
        }} className="text-white/80 text-sm md:text-base tracking-[0.3em] uppercase mb-8 text-center font-light" data-id="element-281">
              Select Your Language / Selecciona Tu Idioma
            </motion.p>

            <div className="flex flex-col sm:flex-row gap-4 sm:gap-6" data-id="element-282">
              <LanguageButton label="ENGLISH" onClick={() => handleLanguageSelect('en')} delay={0.4} data-id="element-283" />
              <LanguageButton label="ESPAÑOL" onClick={() => handleLanguageSelect('es')} delay={0.5} data-id="element-284" />
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}
function ECGAnimation() {
  return <div className="relative h-32 flex items-center justify-center" data-id="element-285">
      {/* ECG Monitor Frame */}
      <div className="absolute inset-0 border border-[#F2B705]/20 rounded-lg bg-[#0a0a0a]/50" data-id="element-286" />

      {/* ECG Line */}
      <svg viewBox="0 0 400 100" className="w-full h-full" preserveAspectRatio="xMidYMid meet" data-id="element-287">
        <defs data-id="element-288">
          <filter id="glow" data-id="element-289">
            <feGaussianBlur stdDeviation="2" result="coloredBlur" data-id="element-290" />
            <feMerge data-id="element-291">
              <feMergeNode in="coloredBlur" data-id="element-292" />
              <feMergeNode in="SourceGraphic" data-id="element-293" />
            </feMerge>
          </filter>
        </defs>

        {/* ECG Path */}
        <motion.path d="M 0 50 L 60 50 L 70 50 L 80 30 L 90 70 L 100 20 L 110 80 L 120 50 L 130 50 L 200 50 L 210 50 L 220 30 L 230 70 L 240 20 L 250 80 L 260 50 L 270 50 L 340 50 L 350 50 L 360 30 L 370 70 L 380 20 L 390 80 L 400 50" fill="none" stroke="#F2B705" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" filter="url(#glow)" initial={{
        pathLength: 0,
        opacity: 0
      }} animate={{
        pathLength: 1,
        opacity: 1
      }} transition={{
        pathLength: {
          duration: 3,
          ease: 'linear'
        },
        opacity: {
          duration: 0.4
        }
      }} data-id="element-294" />

        {/* Scanning dot */}
        <motion.circle r="4" fill="#F2B705" filter="url(#glow)" initial={{
        opacity: 0
      }} animate={{
        opacity: [0, 1, 1, 0]
      }} transition={{
        duration: 3,
        times: [0, 0.05, 0.9, 1]
      }} data-id="element-295">
          <animateMotion dur="3s" repeatCount="1" path="M 0 50 L 60 50 L 70 50 L 80 30 L 90 70 L 100 20 L 110 80 L 120 50 L 130 50 L 200 50 L 210 50 L 220 30 L 230 70 L 240 20 L 250 80 L 260 50 L 270 50 L 340 50 L 350 50 L 360 30 L 370 70 L 380 20 L 390 80 L 400 50" data-id="element-296" />
        </motion.circle>
      </svg>

      {/* Pulse indicator — English (top left) */}
      <motion.div className="absolute top-2 left-4 flex items-center gap-2" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 0.8
    }} data-id="element-297">
        <motion.div className="w-2 h-2 rounded-full bg-[#F2B705]" animate={{
        scale: [1, 1.3, 1],
        opacity: [1, 0.7, 1]
      }} transition={{
        duration: 1,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-298" />
        <span className="text-[#F2B705] text-xs font-mono" data-id="element-299">
          VITAL DIAGNOSIS
        </span>
      </motion.div>

      {/* Pulse indicator — Spanish (bottom right) */}
      <motion.div className="absolute bottom-2 right-4 flex items-center gap-2" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 0.8
    }} data-id="element-300">
        <motion.div className="w-2 h-2 rounded-full bg-[#F2B705]" animate={{
        scale: [1, 1.3, 1],
        opacity: [1, 0.7, 1]
      }} transition={{
        duration: 1,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-301" />
        <span className="text-[#F2B705] text-xs font-mono" data-id="element-302">
          DIAGNÓSTICO VITAL
        </span>
      </motion.div>
    </div>;
}
function LogoReveal() {
  return <motion.div className="relative" data-id="element-303">
      {/* Flicker effect container — slow, cinematic reveal */}
      <motion.div animate={{
      opacity: [0, 0.4, 0.2, 0.6, 0.4, 0.8, 1]
    }} transition={{
      duration: 1.6,
      times: [0, 0.15, 0.25, 0.4, 0.55, 0.75, 1],
      ease: 'easeOut'
    }} data-id="element-304">
        <motion.img src={LOGO_URL} alt="Doctor Ecommerce" className="h-32 md:h-44 rounded-xl object-contain" style={{
        imageRendering: 'auto'
      }} initial={{
        scale: 0.92,
        filter: 'brightness(1.8)'
      }} animate={{
        scale: 1,
        filter: 'brightness(1)'
      }} transition={{
        duration: 1.2,
        delay: 0.4,
        ease: 'easeOut'
      }} data-id="element-305" />
      </motion.div>

      {/* Scan line effect — slower sweep */}
      <motion.div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#F2B705]/20 to-transparent" initial={{
      y: '-100%'
    }} animate={{
      y: '200%'
    }} transition={{
      duration: 1.4,
      ease: 'linear',
      delay: 0.2
    }} data-id="element-306" />

      {/* Glow pulse — slower, more elegant */}
      <motion.div className="absolute -inset-4 rounded-xl" style={{
      background: 'radial-gradient(circle, rgba(242,183,5,0.3) 0%, transparent 70%)'
    }} animate={{
      opacity: [0, 0.5, 0],
      scale: [0.8, 1.15, 1.3]
    }} transition={{
      duration: 1.8,
      ease: 'easeOut'
    }} data-id="element-307" />
    </motion.div>;
}
function LanguageButton({
  label,
  onClick,
  delay
}: {
  label: string;
  onClick: () => void;
  delay: number;
}) {
  return <motion.button initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay,
    duration: 0.4
  }} whileHover={{
    scale: 1.02
  }} whileTap={{
    scale: 0.98
  }} onClick={onClick} className="relative group px-10 py-4 min-w-[180px] bg-black/50 backdrop-blur-sm overflow-hidden" data-id="element-308">
      {/* Animated border */}
      <div className="absolute inset-0 rounded-none" data-id="element-309">
        {/* Border container */}
        <div className="absolute inset-0 border border-[#F2B705]/60" data-id="element-310" />

        {/* Animated shine effect - top */}
        <motion.div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#F2B705] to-transparent" animate={{
        x: ['-100%', '100%']
      }} transition={{
        duration: 2,
        repeat: Infinity,
        ease: 'linear',
        delay: delay * 2
      }} data-id="element-311" />

        {/* Animated shine effect - bottom */}
        <motion.div className="absolute bottom-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#F2B705] to-transparent" animate={{
        x: ['100%', '-100%']
      }} transition={{
        duration: 2,
        repeat: Infinity,
        ease: 'linear',
        delay: delay * 2 + 1
      }} data-id="element-312" />

        {/* Animated shine effect - left */}
        <motion.div className="absolute top-0 left-0 w-[2px] h-full bg-gradient-to-b from-transparent via-[#F2B705] to-transparent" animate={{
        y: ['100%', '-100%']
      }} transition={{
        duration: 2,
        repeat: Infinity,
        ease: 'linear',
        delay: delay * 2 + 0.5
      }} data-id="element-313" />

        {/* Animated shine effect - right */}
        <motion.div className="absolute top-0 right-0 w-[2px] h-full bg-gradient-to-b from-transparent via-[#F2B705] to-transparent" animate={{
        y: ['-100%', '100%']
      }} transition={{
        duration: 2,
        repeat: Infinity,
        ease: 'linear',
        delay: delay * 2 + 1.5
      }} data-id="element-314" />
      </div>

      {/* Hover glow */}
      <motion.div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{
      background: 'radial-gradient(circle at center, rgba(242,183,5,0.15) 0%, transparent 70%)'
    }} data-id="element-315" />

      {/* Button text */}
      <span className="relative z-10 text-white font-semibold tracking-[0.2em] text-sm" data-id="element-316">
        {label}
      </span>
    </motion.button>;
}